import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, Mail, ShieldCheck, ArrowRight, Check } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import PageSEO from '../components/PageSEO';
const PLAN_DETAILS = {
  monitor: { name: 'Monitor', monthly: '$79', annual: '$790', per: '/mo' },
  adviser: { name: 'Adviser', monthly: '$249', annual: '$2490', per: '/mo' },
  agency:       { name: 'Agency',       monthly: '$99', annual: '$79', per: '/mo' },
};

export default function PaymentSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const plan = searchParams.get('plan') || 'monitor';
  const isAnnual = searchParams.get('annual') === 'true';
  const planInfo = PLAN_DETAILS[plan] || PLAN_DETAILS.monitor;

  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address (missing @ or .)');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/payment/send-code', { email, plan, is_annual: isAnnual });
      toast.success('Verification code sent! Check your inbox.');
      setStep('code');
      setCountdown(60);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post('/payment/verify-code', { email, code, plan, is_annual: isAnnual });
      const paymentToken = res.data.payment_token;
      // Redirect to Stripe checkout
      const checkoutRes = await axios.post('/payment/checkout', { payment_token: paymentToken });
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      await axios.post('/payment/send-code', { email, plan, is_annual: isAnnual });
      toast.success('New code sent!');
      setCountdown(60);
    } catch (err) {
      toast.error('Failed to resend. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background transition-colors flex flex-col">
      <PageSEO title="Checkout | AI Search Advisers" isPrivate={true} />
      <GlobalHeader />

      <div className="flex-grow flex items-center justify-center px-6 pt-28 pb-16">
        <div className="w-full max-w-lg">
          
          {/* Plan Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 bg-slate-50 dark:bg-card border border-slate-200 dark:border-border rounded-2xl px-6 py-4">
              <div className="w-10 h-10 bg-[#FEAB3D] rounded-xl flex items-center justify-center">
                <Check className="w-5 h-5 text-[#021426]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Selected Plan</p>
                <p className="font-bold text-slate-900 dark:text-white">
                  {planInfo.name} — {isAnnual ? planInfo.annual : planInfo.monthly}
                  <span className="text-slate-500 font-normal">{planInfo.per}</span>
                  {isAnnual && <span className="ml-2 text-xs bg-[#FEAB3D] text-[#021426] px-2 py-0.5 rounded-full font-bold">Annual</span>}
                </p>
              </div>
            </div>
          </div>

          {/* Progress steps */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {[
              { label: 'Email', icon: Mail },
              { label: 'Verify', icon: ShieldCheck },
              { label: 'Payment', icon: ArrowRight },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                <div className={`flex items-center gap-2 text-sm font-medium transition-colors ${i === 0 ? (step === 'email' ? 'text-[#021426] dark:text-[#FEAB3D]' : 'text-slate-400') : (step === 'code' && i === 1 ? 'text-[#021426] dark:text-[#FEAB3D]' : 'text-slate-400')}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs border-2 transition-all ${(i === 0 && step === 'email') || (i === 1 && step === 'code') ? 'border-[#FEAB3D] bg-[#FEAB3D] text-[#021426]' : (i === 0 && step === 'code') ? 'border-[#021426] bg-[#021426] text-white' : 'border-slate-300 dark:border-slate-600 text-slate-400'}`}>
                    {i === 0 && step === 'code' ? '✓' : i + 1}
                  </div>
                  <span className="hidden sm:block">{s.label}</span>
                </div>
                {i < 2 && <div className="w-8 h-0.5 bg-slate-200 dark:bg-slate-700" />}
              </React.Fragment>
            ))}
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-card rounded-3xl border border-slate-200 dark:border-border shadow-xl p-8">
            {step === 'email' ? (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Enter your email</h2>
                  <p className="text-slate-500 dark:text-slate-400">We'll send a verification code to confirm it's you before payment.</p>
                </div>
                <form onSubmit={handleSendCode} className="space-y-5">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 mb-1.5 block">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="h-12 text-base"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-bold text-base rounded-none"
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                </form>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-4 text-center">
                  By continuing, you agree to our Terms of Service and Privacy Policy.
                </p>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Check your inbox</h2>
                  <p className="text-slate-500 dark:text-slate-400">
                    We sent a 6-digit code to <span className="font-semibold text-slate-700 dark:text-slate-200">{email}</span>
                  </p>
                </div>
                <form onSubmit={handleVerifyCode} className="space-y-5">
                  <div>
                    <Label htmlFor="code" className="text-slate-700 dark:text-slate-300 mb-1.5 block">Verification Code</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                      required
                      className="h-12 text-2xl tracking-widest text-center font-bold"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="w-full h-12 bg-[#021426] text-white hover:bg-[#0A4378] dark:bg-[#FEAB3D] dark:text-[#021426] dark:hover:bg-[#e8960a] font-bold text-base rounded-none"
                  >
                    {loading ? 'Verifying...' : 'Verify & Continue to Payment →'}
                  </Button>
                </form>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => { setStep('email'); setCode(''); }}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  >
                    ← Change email
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={countdown > 0 || loading}
                    className={`font-medium transition-colors ${countdown > 0 ? 'text-slate-400 cursor-not-allowed' : 'text-[#021426] dark:text-[#FEAB3D] hover:underline cursor-pointer'}`}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                  </button>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
            Already have an account?{' '}
            <button onClick={() => navigate('/auth')} className="text-[#021426] dark:text-[#FEAB3D] font-semibold hover:underline">
              Sign in
            </button>
          </p>
        </div>
      </div>
      <GlobalFooter />
    </div>
  );
}
